/**
 * 자동 알림 탭 데이터 훅 — 트리거 카탈로그 + 트리거별 규칙(단건).
 *
 * be-notif Task#3 공표 계약:
 *  - 카탈로그: `GET /admin/v1/notifications/triggers/catalog` → `{items: TriggerCatalogItem[]}`
 *  - 규칙 상세/갱신: `GET·PUT /admin/v1/notification/rules/{triggerCode}` (단건, 목록 엔드포인트 없음)
 *  - 트리거 ON/OFF 목록은 기존 useNotificationTriggersQuery(plural triggers) 가 담당.
 * raw 엔드포인트라 adminFetch(401 자동복구). 쓰기는 Idempotency-Key.
 */
import { useMutation, useQuery, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { adminFetch } from '@/lib/apiClient';
import { idempotencyKeyFor } from '@/lib/http-core';
import type { NotificationChannel } from '../types/notificationMessage';
import type { TemplateLanguageCode } from '../types/notificationTemplate';
import type {
  NotificationRule,
  NotificationRuleUpdatePayload,
  TemplateBinding,
  TriggerCatalogItem,
  TriggerVariable,
} from '../types/notificationRule';

// 트리거 카탈로그는 plural `/admin/v1/notifications/triggers` (게이트웨이 /api strip, 기존 triggers 와 동일).
const TRIGGERS_PLURAL = '/api/admin/v1/notifications/triggers';
const CATALOG_PATH = `${TRIGGERS_PLURAL}/catalog`;
// 규칙은 singular `/admin/v1/notification/rules/{triggerCode}` (no /api — 기존 templates 와 동일 규칙).
const RULES_BASE = '/admin/v1/notification/rules';

interface Envelope<T> {
  success: boolean;
  data?: T | null;
  error?: { code?: string; message?: string } | null;
}

export class AutoNotificationApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  constructor(status: number, code: string | undefined, message: string) {
    super(message);
    this.name = 'AutoNotificationApiError';
    this.status = status;
    this.code = code;
  }
}

const readEnvelope = async <T>(res: Response, fallback: string): Promise<T> => {
  let env: Envelope<T> | null = null;
  try {
    env = (await res.json()) as Envelope<T>;
  } catch {
    env = null;
  }
  if (!res.ok || !env || env.success !== true || env.data == null) {
    throw new AutoNotificationApiError(res.status, env?.error?.code, env?.error?.message ?? fallback);
  }
  return env.data;
};

type Rec = Record<string, unknown>;
const str = (v: unknown): string | null => (typeof v === 'string' && v.length > 0 ? v : null);

const VALID_CHANNELS: ReadonlyArray<string> = ['EMAIL', 'SMS', 'KAKAO', 'KAKAO_BIZ'];
const asChannel = (v: unknown): NotificationChannel | null =>
  typeof v === 'string' && VALID_CHANNELS.includes(v) ? (v as NotificationChannel) : null;
const asChannels = (v: unknown): NotificationChannel[] => {
  const list = Array.isArray(v) ? v : typeof v === 'string' ? v.split(',') : [];
  return list.map((c) => asChannel(String(c).trim())).filter((c): c is NotificationChannel => c !== null);
};

const asVariables = (v: unknown): TriggerVariable[] => {
  if (!Array.isArray(v)) return [];
  return v
    .map((item): TriggerVariable | null => {
      if (typeof item === 'string') return { name: item.replace(/^#\{|\}$/g, '').trim() };
      if (item && typeof item === 'object') {
        const rec = item as Rec;
        const name = typeof rec.name === 'string' ? rec.name : typeof rec.key === 'string' ? rec.key : null;
        if (name) return { name, label: str(rec.label) };
      }
      return null;
    })
    .filter((x): x is TriggerVariable => x !== null);
};

const normalizeCatalogItem = (raw: Rec): TriggerCatalogItem => ({
  triggerCode: String(raw.triggerCode ?? ''),
  label: String(raw.label ?? raw.triggerCode ?? ''),
  category: String(raw.category ?? 'etc'),
  categoryLabel: String(raw.categoryLabel ?? raw.category ?? ''),
  wired: raw.wired === true,
  supportedChannels: asChannels(raw.supportedChannels),
  variables: asVariables(raw.variables),
});

const VALID_LANGUAGES: ReadonlyArray<string> = ['KO_KR', 'EN_US', 'JA_JP', 'ZH_CN'];
const asLanguage = (v: unknown): TemplateLanguageCode | null =>
  typeof v === 'string' && VALID_LANGUAGES.includes(v) ? (v as TemplateLanguageCode) : null;

const normalizeBinding = (raw: Rec): TemplateBinding | null => {
  const channel = asChannel(raw.channel);
  if (!channel) return null;
  return {
    channel,
    channelLabel: str(raw.channelLabel),
    // 구형 응답(languageCode 없음)은 null → 호출부가 전 언어로 펼친다(back-compat).
    languageCode: asLanguage(raw.languageCode),
    templateId: str(raw.templateId),
    templateCode: str(raw.templateCode),
    // 구형 응답(enabled 없음)은 활성으로 간주(true). 명시 false 만 off.
    enabled: raw.enabled === undefined || raw.enabled === null ? true : raw.enabled === true,
  };
};

const normalizeRule = (raw: Rec): NotificationRule => ({
  triggerCode: String(raw.triggerCode ?? ''),
  label: String(raw.label ?? raw.triggerCode ?? ''),
  enabled: raw.enabled === true,
  channels: asChannels(raw.channels),
  templateBindings: Array.isArray(raw.templateBindings)
    ? (raw.templateBindings as Rec[]).map(normalizeBinding).filter((b): b is TemplateBinding => b !== null)
    : [],
  smsFallbackEnabled: raw.smsFallbackEnabled === true,
  smsFallbackTemplateId: str(raw.smsFallbackTemplateId),
  delayMinutes: typeof raw.delayMinutes === 'number' ? raw.delayMinutes : 0,
  senderNumberId: str(raw.senderNumberId),
  kakaoChannelId: str(raw.kakaoChannelId),
});

const asArray = (data: unknown): Rec[] => {
  if (Array.isArray(data)) return data as Rec[];
  if (data && typeof data === 'object') {
    const rec = data as Rec;
    if (Array.isArray(rec.items)) return rec.items as Rec[];
    if (Array.isArray(rec.content)) return rec.content as Rec[];
  }
  return [];
};

// ── 트리거 카탈로그 ─────────────────────────────────────────────
export const triggerCatalogQueryKey = () => ['admin', 'notification', 'trigger-catalog'] as const;

export const useTriggerCatalogQuery = () =>
  useQuery<TriggerCatalogItem[]>({
    queryKey: triggerCatalogQueryKey(),
    queryFn: async () => {
      const res = await adminFetch(CATALOG_PATH);
      const data = await readEnvelope<unknown>(res, '트리거 카탈로그를 불러오지 못했습니다');
      return asArray(data).map(normalizeCatalogItem);
    },
    staleTime: 5 * 60_000,
  });

// ── 규칙(단건) ─────────────────────────────────────────────────
export const notificationRuleQueryKey = (triggerCode: string) =>
  ['admin', 'notification', 'rule', triggerCode] as const;

export const useNotificationRuleQuery = (triggerCode: string | null) =>
  useQuery<NotificationRule>({
    queryKey: notificationRuleQueryKey(triggerCode ?? ''),
    queryFn: async () => {
      const res = await adminFetch(`${RULES_BASE}/${encodeURIComponent(triggerCode ?? '')}`);
      return normalizeRule(await readEnvelope<Rec>(res, '자동 알림 규칙을 불러오지 못했습니다'));
    },
    enabled: Boolean(triggerCode),
    staleTime: 30_000,
  });

export interface UpdateRuleInput {
  triggerCode: string;
  body: NotificationRuleUpdatePayload;
}

export const useUpdateNotificationRuleMutation = (): UseMutationResult<
  NotificationRule,
  AutoNotificationApiError,
  UpdateRuleInput
> => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ triggerCode, body }) => {
      const path = `${RULES_BASE}/${encodeURIComponent(triggerCode)}`;
      const res = await adminFetch(path, {
        method: 'PUT',
        headers: { 'Idempotency-Key': idempotencyKeyFor('PUT', path, body) },
        body: JSON.stringify(body),
      });
      return normalizeRule(await readEnvelope<Rec>(res, '자동 알림 설정에 실패했습니다'));
    },
    onSuccess: (_data, { triggerCode }) => {
      void qc.invalidateQueries({ queryKey: notificationRuleQueryKey(triggerCode) });
    },
  });
};
